// src/utils/CustomDateRangePicker.jsx
import React, { useState, useRef } from 'react'
import {
  Box,
  Button,
  Popover,
  Typography,
  IconButton,
  Select,
  MenuItem,
  TextField,
  Paper,
  ListItemButton,
} from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import {
  format,
  startOfToday,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  isSameDay,
  isWithinInterval,
  addDays,
} from 'date-fns'

const PRESET_RANGES = [
  { label: 'Today', get: () => { const t = startOfToday(); return [t, t] } },
  { label: 'Yesterday', get: () => { const y = subDays(startOfToday(), 1); return [y, y] } },
  {
    label: 'This Week', get: () => {
      const t = startOfToday()
      return [startOfWeek(t), endOfWeek(t)]
    }
  },
  {
    label: 'Last Week', get: () => {
      const t = subDays(startOfToday(), 7)
      return [startOfWeek(t), endOfWeek(t)]
    }
  },
  {
    label: 'This Month', get: () => {
      const t = startOfToday()
      return [startOfMonth(t), endOfMonth(t)]
    }
  },
  {
    label: 'Last Month', get: () => {
      const t = subMonths(startOfToday(), 1)
      return [startOfMonth(t), endOfMonth(t)]
    }
  },
]

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function generateCalendar(monthDate) {
  const start = startOfWeek(startOfMonth(monthDate))
  const end = endOfWeek(endOfMonth(monthDate))
  const days = []
  let cursor = start

  while (cursor <= end) {
    days.push(cursor)
    cursor = addDays(cursor, 1)
  }

  const weeks = []
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7))
  }
  return weeks
}

/**
 * BeautifulDateRangePicker
 *
 * Props:
 *  - selectedRange: [Date,Date]
 *  - onRangeChange: fn([Date,Date])
 *  - minDate, maxDate: Date
 *  - label: string
 */
export default function CustomDateRangePicker({
  selectedRange = [startOfToday(), startOfToday()],
  onRangeChange,
  minDate = new Date(2000, 0, 1),
  maxDate = new Date(new Date().setHours(23, 59, 59, 999)),
  label = 'Created Date',
}) {
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [tempStart, setTempStart] = useState(selectedRange[0])
  const [tempEnd, setTempEnd] = useState(selectedRange[1])
  const [viewDate, setViewDate] = useState(startOfMonth(selectedRange[0]))
  const [upToDays, setUpToDays] = useState('')
  const [startDays, setStartDays] = useState('')

  const handleOpen = () => {
    setTempStart(selectedRange[0])
    setTempEnd(selectedRange[1])
    setViewDate(startOfMonth(selectedRange[0]))
    setOpen(true)
  }
  const handleClose = () => setOpen(false)

  const handleDateClick = date => {
    if (!tempStart || (tempStart && tempEnd)) {
      setTempStart(date)
      setTempEnd(null)
    } else {
      if (date < tempStart) {
        setTempEnd(tempStart)
        setTempStart(date)
      } else {
        setTempEnd(date)
      }
    }
  }

  const handleApply = () => {
    const end = tempEnd || tempStart
    onRangeChange([tempStart, end])
    handleClose()
  }

  const handlePreset = preset => {
    const [s, e] = preset.get()
    setTempStart(s)
    setTempEnd(e)
    setViewDate(startOfMonth(s))
  }

  const applyUpTo = () => {
    const n = parseInt(upToDays, 10)
    if (!isNaN(n)) {
      const end = startOfToday()
      const start = subDays(end, n - 1)
      setTempStart(start)
      setTempEnd(end)
      setViewDate(startOfMonth(start))
    }
  }
  const applyStart = () => {
    const n = parseInt(startDays, 10)
    if (!isNaN(n)) {
      const start = startOfToday()
      const end = addDays(start, n - 1)
      setTempStart(start)
      setTempEnd(end)
      setViewDate(startOfMonth(start))
    }
  }

  return (
    <>
      <Paper
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          p: 1, pl: 2, pr: 1,
          border: '1px solid #ccc',
          borderRadius: 1,
          cursor: 'pointer',
        }}
      >
        <Typography>
          {format(selectedRange[0], 'dd LLL yyyy')}
          &nbsp;–&nbsp;
          {format(selectedRange[1], 'dd LLL yyyy')}
        </Typography>
        <CalendarTodayIcon sx={{ ml: 1, color: '#555' }} />
      </Paper>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { width: 720, height: 400, p: 2 } }}
      >
        <Box sx={{ display: 'flex', height: '100%' }}>
          <Paper
            variant="outlined"
            sx={{ width: 220, mr: 2, p: 1, overflowY: 'auto' }}
          >
            {PRESET_RANGES.map(p => (
              <ListItemButton
                key={p.label}
                onClick={() => handlePreset(p)}
                selected={
                  tempStart && tempEnd &&
                  isSameDay(tempStart, p.get()[0]) &&
                  isSameDay(tempEnd, p.get()[1])
                }
              >
                <Typography>{p.label}</Typography>
              </ListItemButton>
            ))}

            <Box mt={2} display="flex" alignItems="center" columnGap={1}>
              <IconButton size="small" disabled>
                <RemoveCircleOutlineIcon />
              </IconButton>
              <Typography variant="caption" sx={{ flexGrow: 1 }}>
                days up to today
              </Typography>
              <TextField
                size="small"
                variant="outlined"
                value={upToDays}
                onChange={e => setUpToDays(e.target.value)}
                onBlur={applyUpTo}
                placeholder="0"
                sx={{ width: 60 }}
              />
            </Box>

            <Box mt={1} display="flex" alignItems="center" columnGap={1}>
              <IconButton size="small" disabled>
                <RemoveCircleOutlineIcon />
              </IconButton>
              <Typography variant="caption" sx={{ flexGrow: 1 }}>
                days starting today
              </Typography>
              <TextField
                size="small"
                variant="outlined"
                value={startDays}
                onChange={e => setStartDays(e.target.value)}
                onBlur={applyStart}
                placeholder="0"
                sx={{ width: 60 }}
              />
            </Box>
          </Paper>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <IconButton onClick={() => setViewDate(subMonths(viewDate, 1))}>
                <ChevronLeftIcon />
              </IconButton>

              <Select
                size="small"
                value={format(viewDate, 'LLLL')}
                onChange={e => {
                  const m = new Date(Date.parse(e.target.value + ' 1,2000')).getMonth()
                  setViewDate(new Date(viewDate.getFullYear(), m, 1))
                }}
                sx={{ mx: 1 }}
              >
                {Array.from({ length: 12 }, (_, i) =>
                  format(new Date(viewDate.getFullYear(), i, 1), 'LLLL')
                ).map(m => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                value={format(viewDate, 'yyyy')}
                onChange={e => {
                  const y = parseInt(e.target.value, 10)
                  setViewDate(new Date(y, viewDate.getMonth(), 1))
                }}
                sx={{ mr: 'auto' }}
              >
                {Array.from({ length: 11 }, (_, i) =>
                  viewDate.getFullYear() - 5 + i
                ).map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>

              <IconButton onClick={() => setViewDate(addMonths(viewDate, 1))}>
                <ChevronRightIcon />
              </IconButton>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7,1fr)',
              textAlign: 'center',
              color: '#888',
            }}>
              {WEEKDAYS.map(d => (
                <Typography key={d} variant="caption">{d}</Typography>
              ))}
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', mt: 1 }}>
              {generateCalendar(viewDate).map((week, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7,1fr)',
                    mb: 0.5
                  }}
                >
                  {week.map((day, j) => {
                    const inRange = tempStart && tempEnd &&
                      isWithinInterval(day, { start: tempStart, end: tempEnd })
                    const isSoloStart = tempStart && !tempEnd &&
                      isSameDay(day, tempStart)
                    const isEdge = isSameDay(day, tempStart) || (tempEnd && isSameDay(day, tempEnd))

                    return (
                      <Box
                        key={j}
                        onClick={() => handleDateClick(day)}
                        sx={theme => ({
                          p: 1,
                          m: 0.2,
                          borderRadius: 1,
                          backgroundColor: inRange || isSoloStart
                            ? theme.palette.primary.main
                            : 'transparent',
                          color: inRange || isEdge
                            ? '#fff'
                            : 'inherit',
                          cursor: 'pointer',
                          textAlign: 'center'
                        })}
                      >
                        <Typography variant="body2">
                          {format(day, 'd')}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
              ))}
            </Box>

            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Button variant="contained" onClick={handleApply}>
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  )
}
